import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

export type ChallengeMethod = 'totp' | 'webauthn' | 'recovery';

export interface ChallengeMethodPickerProps {
  active: ChallengeMethod;
  onChange: (next: ChallengeMethod) => void;
}

const styles = stylex.create({
  bar: {
    display: 'inline-flex',
    gap: '0.25rem',
    padding: '0.25rem',
    borderRadius: '0.625rem',
    backgroundColor: sentinel.surfaceOverlay,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    width: 'fit-content',
  },
  tab: {
    appearance: 'none',
    cursor: 'pointer',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: sentinel.textMuted,
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
    borderWidth: 0,
    transitionProperty: 'background-color, color, box-shadow, transform',
    transitionDuration: '120ms',
    transitionTimingFunction: sentinel.easeOut,
  },
  tabActive: {
    color: sentinel.textBody,
    fontWeight: 600,
    backgroundColor: sentinel.surfaceElevated,
    boxShadow:
      `inset 0 0 0 1px ${sentinel.brandPrimaryMuted}, 0 6px 16px -8px ${sentinel.brandPrimary}`,
  },
});

export function ChallengeMethodPicker({ active, onChange }: ChallengeMethodPickerProps) {
  const { t } = useTranslation('mfa');

  function tabProps(method: ChallengeMethod, label: string, testid: string) {
    const isActive = active === method;
    return {
      type: 'button' as const,
      role: 'tab',
      'aria-selected': isActive,
      'aria-controls': `mfa-challenge-panel-${method}`,
      id: `mfa-challenge-tab-${method}`,
      'data-testid': testid,
      onClick: () => onChange(method),
      ...stylex.props(styles.tab, isActive && styles.tabActive),
      children: label,
    };
  }

  return (
    <div role="tablist" aria-label={t('verify_title')} {...stylex.props(styles.bar)}>
      <button {...tabProps('totp', t('method_totp'), 'mfa-challenge-tab-totp')} />
      <button {...tabProps('webauthn', t('method_webauthn'), 'mfa-challenge-tab-webauthn')} />
      <button {...tabProps('recovery', t('method_recovery'), 'mfa-challenge-tab-recovery')} />
    </div>
  );
}
